import * as React from 'react';

import type { LearnerStatus } from '@coursera-web/enterprise-admin-learners';

import { getDefaultUserFilterPreset } from 'bundles/enterprise-admin-user-filters/constants';
import type { SavedAdminFilter } from 'bundles/enterprise-admin-user-filters/types';

export type User = {
  userId: string;
  fullName: string;
  email: string;
  status?: LearnerStatus;
  externalId?: string;
};

/**
 * One target program for the "selected learners, multi-program" Assign content flow
 * (ENTADMIN-1870, All Learners entry point): `explicit` carries the exact learners to
 * assign in that program (built client-side from the selection — full `User` records,
 * not just IDs, so invited learners can still be routed through the `invitees` input at
 * submit time); `select-all` means "every learner matching the base filter, scoped to
 * this program" — the scoped saved filter is created at submit time from
 * `contentAssignmentFilter` + `programId`.
 */
export type ContentAssignmentBucket =
  | { programId: string; programName?: string; mode: 'explicit'; assignees: User[] }
  | { programId: string; programName?: string; mode: 'select-all' };

export type UserFilterContextType = {
  selectedUsers: User[];
  setSelectedUsers: (value: User[]) => void;
  userFilter: SavedAdminFilter;
  setUserFilter: React.Dispatch<React.SetStateAction<SavedAdminFilter>>;
  contentAssignmentFilter?: SavedAdminFilter;
  setContentAssignmentFilter: React.Dispatch<React.SetStateAction<SavedAdminFilter | undefined>>;
  contentAssignmentProgramId?: string;
  setContentAssignmentProgramId: React.Dispatch<React.SetStateAction<string | undefined>>;
  contentAssignmentSearch: string;
  setContentAssignmentSearch: React.Dispatch<React.SetStateAction<string>>;
  contentAssignmentBuckets?: ContentAssignmentBucket[];
  setContentAssignmentBuckets: React.Dispatch<React.SetStateAction<ContentAssignmentBucket[] | undefined>>;
};

export const UserFilterContext = React.createContext<UserFilterContextType | undefined>(undefined);

export const UserFilterProvider = ({
  defaultFilter = getDefaultUserFilterPreset(),
  children,
}: React.PropsWithChildren<{ defaultFilter?: SavedAdminFilter }>) => {
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const [userFilter, setUserFilter] = React.useState<SavedAdminFilter>(defaultFilter);
  const [selectedUsers, setSelectedUsers] = React.useState<User[]>([]);
  const [contentAssignmentFilter, setContentAssignmentFilter] = React.useState<SavedAdminFilter | undefined>(undefined);
  const [contentAssignmentProgramId, setContentAssignmentProgramId] = React.useState<string | undefined>(undefined);
  const [contentAssignmentSearch, setContentAssignmentSearch] = React.useState<string>('');
  const [contentAssignmentBuckets, setContentAssignmentBuckets] = React.useState<ContentAssignmentBucket[] | undefined>(
    undefined
  );
  return (
    <UserFilterContext.Provider
      value={{
        selectedUsers,
        setSelectedUsers,
        userFilter,
        setUserFilter,
        contentAssignmentFilter,
        setContentAssignmentFilter,
        contentAssignmentProgramId,
        setContentAssignmentProgramId,
        contentAssignmentSearch,
        setContentAssignmentSearch,
        contentAssignmentBuckets,
        setContentAssignmentBuckets,
      }}
    >
      {children}
    </UserFilterContext.Provider>
  );
};

export const useUserFilter = () => {
  const context = React.useContext(UserFilterContext);
  if (context === undefined) {
    throw new Error('useUserFilter must be used within a UserFilterContext');
  }
  return context;
};
